<template>
  <div class="comenzi-container">
    <div class="filters-bar">
      <FiltreComenzi @filtersChanged="updateFilters" />
    </div>

    <DxTabPanel
      :selected-index="activeMainTab"
      :data-source="mainTabs"
      :animation-enabled="false"
      :loop="false"
      :swipe-enabled="false"
      @selection-changed="onMainTabChanged"
      height="100%"
    >
      <template #item="{ data }">
        <div v-if="data.id === 'comenzi'" class="tab-content">
          <GridComenzi
            :filters="filters"
            @openOrder="openOrder"
            class="GridComenzi"
          />
        </div>

        <div v-else class="tab-content">
          <div v-if="openOrders.length === 0" class="no-orders">
            Nicio comandă deschisă.
          </div>

          <div v-else class="subtabs-wrapper">
            <DxTabPanel
              :selected-index="activeSubTab"
              :data-source="openOrders"
              :animation-enabled="false"
              :loop="false"
              :swipe-enabled="false"
              @selection-changed="onSubTabChanged"
              height="100%"
              class="subtabs-panel"
            >
              <template #title="{ data, index }">
                <div class="subtab-header" v-if="data && data.title">
                  <span>{{ data.title }}</span>
                  <DxButton
                    icon="close"
                    text=""
                    styling-mode="text"
                    @click="() => closeSubTab(index)"
                  />
                </div>
              </template>

              <template #item="{ data }">
                <div class="tab-content">
                  <GridComandaDetalii
                    :key="data.orderId"
                    :comandaId="data.orderId"
                    class="GridComandaDetalii"
                  />
                </div>
              </template>
            </DxTabPanel>
          </div>
        </div>
      </template>
    </DxTabPanel>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import DxTabPanel from "devextreme-vue/tab-panel";
import DxButton from "devextreme-vue/button";
import FiltreComenzi from "./FilterComenzi.vue";
import GridComenzi from "./GridComenzi.vue";
import GridComandaDetalii from "./GridComandaDetalii.vue";

interface Filter {
  category: number | null;
  start: string;
  end: string;
}

// Interfață ASUMATĂ a întregului obiect de date (folosită de GridComenzi)
interface OrderData {
  Id: string | number; // Putem avea si number, in functie de DB
  Cod: string;
  Nume: string;
  DataComenzii: string;
  ValoareComanda: number;
  [key: string]: any; // Permite orice alte câmpuri
}

// Interfață pentru un tab deschis (păstrează doar metadatele necesare tab-ului)
interface OpenOrderTab {
  id: string;
  orderId: string;
  title: string;
  // Putem adăuga și alte metadate relevante pentru UI, de exemplu:
  // clientName: string;
}

const route = useRoute();

const filters = ref<Filter>({ category: null, start: "", end: "" });

const mainTabs = [
  { id: "comenzi", title: "Comenzi" },
  { id: "detalii", title: "Detalii Comenzi" },
];

const activeMainTab = ref(0);
const openOrders = ref<OpenOrderTab[]>([]);
const activeSubTab = ref(0);
let uniqueTabCounter = 0;

/**
 * Actualizează filtrele și șterge toate sub-taburile deschise.
 */
function updateFilters(newFilters: Filter) {
  filters.value = newFilters;

  // Șterge taburile deschise
  openOrders.value = [];
  activeSubTab.value = 0;
  activeMainTab.value = 0;
}

function onMainTabChanged(e: any) {
  activeMainTab.value = e.component.option("selectedIndex");
}

function onSubTabChanged(e: any) {
  activeSubTab.value = e.component.option("selectedIndex");
}

/**
 * Deschide o comandă nouă într-un sub-tab separat.
 * Acum primește întregul obiect de comandă (OrderData).
 */
function openOrder(order: OrderData) {
  const comandaIdString = String(order.Id);

  // Verifică dacă tab-ul este deja deschis pentru a evita duplicatele
  const existingIndex = openOrders.value.findIndex(t => t.orderId === comandaIdString);
  if (existingIndex !== -1) {
    activeSubTab.value = existingIndex;
    activeMainTab.value = 1;
    return;
  }

  uniqueTabCounter++;

  const newTab: OpenOrderTab = {
    id: `tab-${comandaIdString}-${uniqueTabCounter}`,
    orderId: comandaIdString,
    // Folosim Codul și Numele Clientului pentru titlul tabului
    title: `${order.Cod}`,
  };

  // Folosim Deep Copy pentru a forța DevExtreme să re-randareze
  const updatedOrders = [...openOrders.value, newTab];
  openOrders.value = updatedOrders;

  activeSubTab.value = openOrders.value.length - 1;

  activeMainTab.value = 1;
}

/**
 * Închide un sub-tab (fără a folosi evenimentul, doar indexul).
 */
function closeSubTab(index: number) {
  // Folosim Deep Copy pentru a forța DevExtreme să actualizeze
  const updatedOrders = openOrders.value.value.filter((_, i) => i !== index);
  openOrders.value = updatedOrders;

  if (activeSubTab.value >= openOrders.value.length) {
    activeSubTab.value = openOrders.value.length - 1;
  }

  if (openOrders.value.length === 0) {
    activeMainTab.value = 0;
  }
}

// LOGICĂ PENTRU DESCHIDEREA AUTOMATĂ DIN RUTĂ
onMounted(() => {
  const comandaIdFromRoute = route.params.comandaId as string;

  if (comandaIdFromRoute) {
    // Aici trebuie să reconstruim un obiect 'OrderData' minim necesar 
    // pentru a apela openOrder, deoarece din rută avem doar ID-ul.
    // Presupunem un Cod și Nume generic, sau ar trebui să se facă un fetch de date.
    const minimalOrderData: OrderData = { 
        Id: comandaIdFromRoute, 
        Cod: comandaIdFromRoute,
        Nume: "Încărcare...",
        DataComenzii: "",
        ValoareComanda: 0,
    };
    openOrder(minimalOrderData);
  }
});
</script>

<style scoped>
.comenzi-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.filters-bar {
  flex-shrink: 0;
  padding: 8px;
}
.tab-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.subtabs-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.subtab-header {
  display: flex;
  align-items: center;
  gap: 4px;
}
.GridComenzi,
.GridComandaDetalii {
  flex-grow: 1;
  height: 100%;
  width: 100%;
}
/* ---------------------------------------------------------------------- */
/* STILURI DEVEXTREME PENTRU SUB-TABURI */
/* ---------------------------------------------------------------------- */

.subtabs-panel >>> .dx-tabpanel-tabs {
  overflow-x: auto;
  white-space: nowrap;
}

/* Aplică lățimea minimă pe fiecare tab */
.subtabs-panel >>> .dx-tabpanel-tabs .dx-tab {
    min-width: 150px;
    max-width: 250px;
    flex-shrink: 0;
}

/* Asigură că textul din interiorul tabului nu este trunchiat */
.subtabs-panel >>> .dx-tabpanel-tabs .dx-tab-text {
    white-space: normal;
    overflow: visible;
}

/* Ajustează alinierea header-ului tabului */
.subtab-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.no-orders {
  padding: 16px;
  color: #666;
}
</style>